// Filterpanel: sökfält + sliders + sector/country dropdowns + reset.
// Skickar state upp till parent via callbacks.

import { useEffect, useState } from 'react';
import RangeSlider from './RangeSlider';
import { fetchDistinctValues } from '../../lib/pocketbase';
import {
  type ScreenerState,
  SCREENER_DEFAULTS,
} from '../../lib/screener';

interface Props {
  state: ScreenerState;
  onChange: (next: ScreenerState) => void;
  onReset: () => void;
}

const fmtCompact = (n: number) => {
  if (n >= 1e12) return `${(n / 1e12).toFixed(1)} T`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(0)} mdr`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(0)} M`;
  return String(n);
};

const BOUNDS = {
  pe: { min: 0, max: 200 },
  div: { min: 0, max: 50, step: 0.1 },
  mcap: { min: 0, max: 10_000_000_000_000, step: 100_000_000 },
  rsi: { min: 0, max: 100 },
} as const;

export default function FilterPanel({ state, onChange, onReset }: Props) {
  const update = (patch: Partial<ScreenerState>) => onChange({ ...state, ...patch, page: 1 });
  const updateRange = (key: 'pe' | 'div' | 'mcap' | 'rsi', range: { min: number | null; max: number | null }) =>
    onChange({ ...state, [key]: range, page: 1 });

  const [sectors, setSectors] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchDistinctValues('stocks', 'sector').catch(() => []),
      fetchDistinctValues('stocks', 'country').catch(() => []),
    ]).then(([s, c]) => {
      if (cancelled) return;
      setSectors(s);
      setCountries(c);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Sök</label>
        <input
          type="search"
          value={state.q}
          onChange={(e) => update({ q: e.target.value })}
          placeholder="Ticker eller namn…"
          className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        />
      </div>

      <RangeSlider
        label="P/E-tal"
        min={state.pe.min}
        max={state.pe.max}
        bounds={BOUNDS.pe}
        format={(n) => n.toFixed(0)}
        onChange={(r) => updateRange('pe', r)}
      />
      <RangeSlider
        label="Direktavkastning (%)"
        min={state.div.min}
        max={state.div.max}
        bounds={BOUNDS.div}
        format={(n) => n.toFixed(1)}
        onChange={(r) => updateRange('div', r)}
      />
      <RangeSlider
        label="Börsvärde"
        min={state.mcap.min}
        max={state.mcap.max}
        bounds={BOUNDS.mcap}
        format={fmtCompact}
        onChange={(r) => updateRange('mcap', r)}
      />
      <RangeSlider
        label="RSI(14)"
        min={state.rsi.min}
        max={state.rsi.max}
        bounds={BOUNDS.rsi}
        format={(n) => n.toFixed(0)}
        onChange={(r) => updateRange('rsi', r)}
      />

      <SelectFilter
        label="Sektor"
        value={state.sector}
        options={sectors}
        onChange={(v) => update({ sector: v })}
      />
      <SelectFilter
        label="Land"
        value={state.country}
        options={countries}
        onChange={(v) => update({ country: v })}
      />

      <button
        type="button"
        onClick={onReset}
        className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        Återställ filter
      </button>
    </div>
  );
}

export { SCREENER_DEFAULTS };

interface SelectFilterProps {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}

function SelectFilter({ label, value, options, onChange }: SelectFilterProps) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-white"
      >
        <option value="">Alla</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}
